import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import { query } from '@trlogic/client-js';
import AiModelClient from '../client/ai/AiModelClient';
import ExpressionBridge from '../util/ExpressionBridge';
import FolderClient from '../client/folder/FolderClient';
import { isStringValid } from '../util/Generator';
import MessageUtil from '../util/MessageUtil';
import React from 'react';
import { Syntax } from '@trlogic/ui-pack';
import _ from 'lodash';
import ComponentName from '../constant/ComponentName';
import LanguageUtil from '../util/LanguageUtil';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { ActionType } from '../constant/Constants';
import { MLOpsModel } from '../domain/mlops/MLOpsDomains';
import SelectQuery = query.SelectQuery;
import DeployErrorUtil from '../util/DeployErrorUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class AiModelService {
  private readonly aiModelClient: AiModelClient;
  private readonly expressionBridge: ExpressionBridge;
  private readonly folderClient: FolderClient;

  constructor(aiModelClient: AiModelClient, expressionBridge: ExpressionBridge, folderClient: FolderClient) {
    this.aiModelClient = aiModelClient;
    this.expressionBridge = expressionBridge;
    this.folderClient = folderClient;
  }

  public async save(
    _item: MLOpsModel,
    isUpdate: boolean,
    folder: EnrichedComponentCategory,
    trainingFile: File | null,
    validationFile: File | null,
  ): Promise<boolean> {
    try {
      const isValid = this.verifyAiModel(_item);
      if (!isValid) {
        return false;
      }
      if (!isUpdate) {
        const isExist = await this.aiModelClient.exist(_item.name);
        if (isExist) {
          MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.error.aiModel' }));
          return false;
        }
      }
      const item: MLOpsModel = _.cloneDeep(_item);
      for (const f of item.customFeatures) {
        f.expression = await this.parseExpression(f.expression);
      }
      item.id = item.name;

      await this.aiModelClient.save(item, trainingFile, validationFile);

      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.AI_MODEL, folder.id, item.name);
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),
        detail: (
          <div>
            <b>{item.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'Saved' })}
          </div>
        ),
      });
      return true;
    } catch (e) {
      console.error(e);
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{_item.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'can not save' })}
          </div>
        ),
      });
      return false;
    }
  }

  public async remove(aiModel: MLOpsModel): Promise<boolean> {
    try {
      const response: any = await this.aiModelClient.remove(aiModel.name);
      if (response.code) {
        return false;
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.AI_MODEL));
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name}</b> {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  }

  public async deploy(aiModel: MLOpsModel) {
    try {
      await this.save(aiModel, true, {} as EnrichedComponentCategory, null, null);
      await this.aiModelClient.deploy(aiModel.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name}</b> {LanguageUtil.get({ id: 'messages.deployed', defaultMessage: 'Deployed' })}
          </div>
        ),
      });
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name}</b> {LanguageUtil.get({ id: 'messages.cannotDeployed', defaultMessage: 'can not deploy' })}
          </div>
        ),
      });
    }
  }

  public async count(query: SelectQuery): Promise<number> {
    return this.aiModelClient.count(query);
  }

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.aiModelClient.bulkAction(ids, type);
    return logs;
  }

  public async undeploy(aiModel: MLOpsModel) {
    try {
      await this.aiModelClient.undeploy(aiModel.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name}</b> {LanguageUtil.get({ id: 'messages.undeployed', defaultMessage: 'undeployed' })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

        detail: (
          <div>
            <b>{aiModel.name} </b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeploy' })}
          </div>
        ),
      });
    }
  }

  private verifyAiModel(aiModel: MLOpsModel): boolean {
    try {
      if (aiModel.name.trim().length == 0 || !isStringValid(aiModel.name)) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

          detail: <div>{LanguageUtil.get({ id: 'messages.error.nameempty', defaultMessage: 'Name cannot be empty!' })}</div>,
        });
        return false;
      }
      if (aiModel.channel == undefined || aiModel.channel.trim().length == 0) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

          detail: <div>{LanguageUtil.get({ id: 'messages.error.channelEmpty', defaultMessage: 'Channel cannot be empty!' })}</div>,
        });
        return false;
      }
      if (aiModel.baseFeatures.length == 0) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

          detail: <div>{LanguageUtil.get({ id: 'messages.error.featuresEmpty', defaultMessage: 'Features cannot be empty!' })}</div>,
        });
        return false;
      }
      if (aiModel.samplingRatio == undefined) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

          detail: <div>{LanguageUtil.get({ id: 'messages.error.samplingRatioEmpty', defaultMessage: 'Sampling ratio cannot be empty!' })}</div>,
        });
        return false;
      }
      if (aiModel.algorithms.length == 0) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.aiModel', defaultMessage: 'Ai Model' }),

          detail: <div>{LanguageUtil.get({ id: 'messages.error.algorithmsEmpty', defaultMessage: 'Algorithms cannot be empty!' })}</div>,
        });
        return false;
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  public async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }
}

export default AiModelService;
